import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Pause, Play, ChevronUp, ChevronDown, Briefcase } from 'lucide-react';
import { useTimeTracking } from '../../contexts/TimeTrackingContext';
import { useTranslation } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';

interface TimeTrackingIndicatorProps {
  className?: string;
}

/**
 * TIME TRACKING INDICATOR
 * 
 * Floating indicator that shows:
 * - Current tracking status (active/paused)
 * - Current case being tracked
 * - Elapsed time for current session
 * - Today's total time
 * 
 * Can be minimized to just show a small icon
 */
const TimeTrackingIndicator: React.FC<TimeTrackingIndicatorProps> = ({ className }) => {
  const {
    isTracking,
    isPaused,
    currentCaseName,
    currentContext,
    formattedElapsed,
    formattedTodayTotal,
    togglePause,
    stopTracking
  } = useTimeTracking();
  
  const { language } = useTranslation();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true); // Default to minimized (just clock icon)
  
  // Translations
  const t = {
    trackingTime: language === 'en' ? 'Tracking time' : 'Sporer tid',
    paused: language === 'en' ? 'Paused' : 'Pauset',
    workingOn: language === 'en' ? 'Working on' : 'Jobber på',
    todayTotal: language === 'en' ? 'Today total' : 'I dag totalt',
    pause: language === 'en' ? 'Pause' : 'Pause',
    resume: language === 'en' ? 'Resume' : 'Fortsett',
    minimize: language === 'en' ? 'Minimize' : 'Minimer',
    expandTooltip: language === 'en' ? 'Time tracking active - click to expand' : 'Tidsregistrering aktiv - klikk for å utvide',
    pausedDueToInactivity: language === 'en' 
      ? 'Paused due to inactivity. Click or type to resume.' 
      : 'Pauset pga. inaktivitet. Klikk eller skriv for å fortsette.'
  };
  
  // Helper to get label for context
  const getContextLabel = (context: string) => {
    const labels: Record<string, { en: string; nb: string }> = {
      'ai_workspace': { en: 'AI Workspace', nb: 'AI Workspace' },
      'inbox': { en: 'Inbox', nb: 'Innboks' },
      'dashboard': { en: 'General', nb: 'Generelt' },
      'case_detail': { en: 'Case Details', nb: 'Saksdetaljer' },
      'general': { en: 'General', nb: 'Generelt' }
    };
    const label = labels[context];
    if (label) {
      return language === 'en' ? label.en : label.nb;
    }
    return context;
  };
  
  // Don't show if not tracking
  if (!isTracking) return null;
  
  // Minimized view - just a small pulsing dot
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={cn(
          "fixed bottom-4 right-4 z-50 p-2 rounded-full shadow-lg transition-all",
          isPaused ? "bg-amber-100" : "bg-green-100",
          className
        )}
        title={t.expandTooltip}
      >
        <div className="relative">
          <Clock className={cn("w-5 h-5", isPaused ? "text-amber-600" : "text-green-600")} />
          {!isPaused && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          )}
        </div>
      </button>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={cn(
        "fixed bottom-4 right-4 z-50 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden",
        isExpanded ? "w-72" : "w-56",
        className
      )}
    >
      {/* Header */}
      <div 
        className={cn(
          "flex items-center justify-between px-3 py-2 cursor-pointer",
          isPaused ? "bg-amber-50" : "bg-green-50"
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <Clock className={cn("w-4 h-4", isPaused ? "text-amber-600" : "text-green-600")} />
            {!isPaused && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            )}
          </div>
          <span className={cn(
            "text-sm font-medium",
            isPaused ? "text-amber-700" : "text-green-700"
          )}>
            {isPaused ? t.paused : t.trackingTime}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-mono font-bold text-gray-700">
            {formattedElapsed}
          </span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-200"
          >
            <div className="p-3 space-y-3">
              {/* Current case/context */}
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">{t.workingOn}</p>
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {currentCaseName || getContextLabel(currentContext)}
                  </p>
                </div>
              </div>
              
              {/* Today's total */}
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <span className="text-xs text-gray-500">{t.todayTotal}</span>
                <span className="text-sm font-mono font-semibold text-gray-700">
                  {formattedTodayTotal}
                </span>
              </div>
              
              {/* Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePause();
                  }}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                    isPaused 
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                  )}
                >
                  {isPaused ? (
                    <>
                      <Play className="w-3.5 h-3.5" />
                      {t.resume}
                    </>
                  ) : (
                    <>
                      <Pause className="w-3.5 h-3.5" />
                      {t.pause}
                    </>
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMinimized(true);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t.minimize}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              {/* Inactivity hint */}
              {isPaused && (
                <p className="text-xs text-amber-600 text-center">
                  {t.pausedDueToInactivity}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TimeTrackingIndicator;
